import { Link } from '@inertiajs/react'
import { Avatar } from '@/components/design-system'
import { Badge } from '@/components/ui/badge'
import { SectionLabel } from '@/frontend/features/Home/SectionLabel'
import { Workflow } from 'lucide-react'
import { PanelEmptyState } from '@/frontend/components/EmptyState'
import { processStatusVariant } from './processStatus'
import type { WorkProcess, WorkerAvatarMap } from './types'

interface Props {
  processes: WorkProcess[]
  // Reuso no portal do agente (Iteração 3, Onda B): a MESMA lista com outro
  // rótulo ("Processos vinculados") e sem a coluna de responsável — no
  // portal todos os processos são deste agente.
  title?: string
  showWorker?: boolean
  workerAvatars?: WorkerAvatarMap
}

/**
 * "Processos" (Iteração 3, Onda A): os business_processes ativos, em LISTA —
 * linhas no mesmo padrão visual das rotinas. Honestidade de dados
 * (INVESTIGACAO-DADOS): o vínculo processo↔rotina NÃO existe, então a linha
 * mostra SÓ nome, status e agente responsável — que são reais. Nada de
 * "última/próxima execução" fabricada. Clique leva à página do processo.
 * Reutilizado pelo dashboard de operação do agente (Onda B) como "Processos
 * vinculados".
 */
export function ProcessesPanel({ processes, title = 'Processos', showWorker = true, workerAvatars = {} }: Props) {
  return (
    <section aria-label={title} className="space-y-2">
      <SectionLabel>
        {title}
        {processes.length > 0 && (
          <span className="rounded-full bg-secondary px-[7px] text-[10.5px] font-semibold text-muted-foreground">
            {processes.length}
          </span>
        )}
      </SectionLabel>

      {processes.length === 0 ? (
        <PanelEmptyState icon={Workflow} title="Nenhum processo ativo" description="Os que estiverem em andamento aparecem aqui." />
      ) : (
        <ul className="overflow-hidden rounded-xl border border-border bg-card">
          {processes.map((process) => (
            <li key={process.id} className="border-b border-border last:border-b-0">
              <Link
                href={process.path}
                className="flex items-center gap-3 px-4 py-3 transition-colors hover:bg-accent"
              >
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-medium text-foreground">{process.name}</p>
                  {showWorker && process.worker_name && process.worker_id && (
                    <div className="flex items-center gap-1.5 text-[11.5px] text-faint-foreground">
                      <Avatar src={workerAvatars[process.worker_id] ?? undefined} alt="" name={process.worker_name} hueSeed={process.worker_id} size="xxs" className="flex-none" />
                      <span className="truncate text-muted-foreground">{process.worker_name}</span>
                    </div>
                  )}
                </div>
                <Badge variant={processStatusVariant(process.status)} className="flex-none">
                  {process.status_label}
                </Badge>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
